import { Link } from 'react-router-dom';
import { ArrowRight, Check, Circle, FileText } from 'lucide-react';
import api, { isSession } from '../../services/api';
import { useFetch } from '../../services/operatorApi';
import { Badge, Card, DocumentStatusBadge, Empty, ErrorBox, PageHeader, Spinner, StatCard, ago, num } from '../../components/ui';

interface Credits {
  billing_mode: 'free' | 'prepaid';
  balance: number;
  committed: number;
  available: number;
}
interface Doc {
  id: string;
  title: string;
  status: string;
  created_at: string;
}
interface Me {
  organization: { name: string };
  active_keys: number;
}

export default function ClientDashboard() {
  const session = isSession();
  const { data, error, loading, reload } = useFetch(async () => {
    const [credits, documents, me] = await Promise.all([
      api.get<Credits>('/credits'),
      api.get<{ documents: Doc[] }>('/documents', { params: { limit: 100 } }),
      session ? api.get<Me>('/account/me') : Promise.resolve(null),
    ]);
    return { credits: credits.data, documents: documents.data.documents ?? [], me: me?.data ?? null };
  });

  if (loading && !data) return <Spinner />;
  if (error || !data) return <ErrorBox message={error ?? 'Chargement impossible'} onRetry={reload} />;

  const { credits, documents, me } = data;
  const count = (...statuses: string[]) => documents.filter((d) => statuses.includes(d.status)).length;
  const low = credits.billing_mode === 'prepaid' && credits.available <= 10;
  const steps = [
    { done: session ? (me?.active_keys ?? 0) > 0 : true, title: "Créer votre clé d'API", text: "C'est elle qui identifie votre application auprès de l'API.", to: '/keys', cta: 'Gérer mes clés' },
    { done: documents.length > 0, title: 'Envoyer un premier document à signer', text: "Suivez l'exemple pas à pas, puis ouvrez le lien reçu pour signer vous-même.", to: '/integration', cta: "Voir l'exemple" },
    { done: count('completed') > 0, title: 'Récupérer le PDF signé et sa preuve', text: 'Disponibles dans Documents dès que tous les signataires ont signé.', to: '/documents', cta: 'Mes documents' },
  ];

  return (
    <div>
      <PageHeader title={me ? `Bonjour, ${me.organization.name}` : 'Tableau de bord'} subtitle="Vos signatures, votre solde et vos premiers pas" />

      {credits.billing_mode === 'prepaid' && (
        <div className={`mb-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border p-5 ${credits.available <= 0 ? 'border-red-200 bg-red-50' : low ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
          <div>
            <p className="text-sm font-medium text-slate-700">Signatures disponibles</p>
            <p className="mt-1 text-3xl font-bold text-slate-900">{num(credits.available)}</p>
            <p className="mt-1 text-sm text-slate-600">
              {num(credits.balance)} au solde, dont {num(credits.committed)} déjà attendues sur des documents en cours.
              {low && ' Pensez à recharger votre lot.'}
            </p>
          </div>
          <Link to="/billing" className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-500">
            Acheter un lot <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Documents" value={num(documents.length)} />
        <StatCard label="En cours" value={num(count('sent', 'in_progress'))} />
        <StatCard label="Signés" value={num(count('completed'))} tone="good" />
        <StatCard label="Annulés ou refusés" value={num(count('voided', 'declined', 'expired'))} />
      </div>

      {steps.some((s) => !s.done) && (
        <Card title="Pour démarrer" className="mt-6">
          <ol className="space-y-4">
            {steps.map((step, index) => (
              <li key={step.title} className="flex items-start gap-3">
                {step.done ? (
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><Check className="h-4 w-4" /></span>
                ) : (
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-300 text-slate-400"><Circle className="h-3 w-3" /></span>
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${step.done ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{index + 1}. {step.title}</p>
                  {!step.done && <p className="mt-0.5 text-sm text-slate-500">{step.text}</p>}
                </div>
                {!step.done && <Link to={step.to} className="shrink-0 text-sm font-medium text-indigo-600 hover:underline">{step.cta}</Link>}
              </li>
            ))}
          </ol>
        </Card>
      )}

      <Card title="Derniers documents" className="mt-6" action={<Link to="/documents" className="text-sm font-medium text-indigo-600 hover:underline">Tout voir</Link>}>
        {documents.length === 0 ? (
          <Empty>Aucun document pour le moment.</Empty>
        ) : (
          <ul className="divide-y divide-slate-100">
            {documents.slice(0, 6).map((doc) => (
              <li key={doc.id} className="flex items-center justify-between gap-3 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <FileText className="h-5 w-5 shrink-0 text-slate-400" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">{doc.title}</p>
                    <p className="text-xs text-slate-500">{ago(doc.created_at)}</p>
                  </div>
                </div>
                <DocumentStatusBadge status={doc.status} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      {!session && (
        <p className="mt-6 text-xs text-slate-500">
          <Badge>Connexion par clé d&apos;API</Badge> La gestion des clés et l&apos;achat de lots demandent une connexion par e-mail : déconnectez-vous puis choisissez « Recevoir un code ».
        </p>
      )}
    </div>
  );
}
